import type { FC, HTMLAttributes, ReactNode } from "react";

import classnames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";
import { Icon } from "@jti/core-components/components/Icon";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";

import styles from "./HeaderNavListItem.module.scss";

type HeaderNavListItemBaseProps = {
  label: string;
  startIcon?: DirectionalIconComponent;
  endIcon?: DirectionalIconComponent;
  isSelected?: boolean;
  selectedIcon?: DirectionalIconComponent;
  isCondensed?: boolean;
  hasTextEmphasized?: boolean;
  id?: string;
  children?: ReactNode;
};

type HeaderNavListItemButtonProps = HeaderNavListItemBaseProps & {
  variant?: "button";
  disabled?: boolean;
} & Omit<HTMLAttributes<HTMLButtonElement>, "children">;

type HeaderNavListItemLinkProps = HeaderNavListItemBaseProps & {
  variant: "link";
  href: string;
  isActive?: boolean;
  className?: string;
} & Omit<HTMLAttributes<HTMLAnchorElement>, "children">;

export type HeaderNavListItemProps = HeaderNavListItemButtonProps | HeaderNavListItemLinkProps;

const ItemContent: FC<{
  label: string;
  id?: string;
  startIcon?: DirectionalIconComponent;
  endIcon?: DirectionalIconComponent;
  isSelected: boolean;
  selectedIcon?: DirectionalIconComponent;
  isCondensed: boolean;
  hasTextEmphasized: boolean;
}> = ({ label, startIcon, endIcon, id, isSelected, selectedIcon, isCondensed, hasTextEmphasized }) => (
  <>
    {startIcon && <Icon icon={startIcon} size="xs" className={styles.leadingIcon} aria-hidden />}
    <span
      className={classnames(
        styles.label,
        hasTextEmphasized && styles.emphasized,
        isCondensed && styles.labelCondensed,
        isCondensed && hasTextEmphasized && styles.labelCondensedEmphasized
      )}
      id={id}
    >
      {label}
    </span>
    {endIcon && <Icon icon={endIcon} size="sm" className={styles.trailingIcon} aria-hidden />}
    {isSelected && selectedIcon && <Icon icon={selectedIcon} size="xs" className={styles.selectedIcon} aria-hidden />}
  </>
);

export const HeaderNavListItem: FC<HeaderNavListItemProps> = (props) => {
  const {
    label,
    startIcon,
    endIcon,
    isSelected = false,
    selectedIcon,
    isCondensed = false,
    hasTextEmphasized = false,
    id,
  } = props;

  const content = (
    <ItemContent
      label={label}
      startIcon={startIcon}
      endIcon={endIcon}
      id={id}
      isSelected={isSelected}
      selectedIcon={selectedIcon}
      isCondensed={isCondensed}
      hasTextEmphasized={hasTextEmphasized}
    />
  );

  if (props.variant === "link") {
    const { href, isActive, className, ...linkRest } = props;

    return (
      <LinkBehaviour
        href={href}
        title={label}
        aria-current={linkRest["aria-current"] ?? (isActive ? "page" : undefined)}
        className={classnames(styles.headerNavListItem, className)}
        {...linkRest}
      >
        {content}
      </LinkBehaviour>
    );
  }

  const { className, ...rest } = props;

  return (
    <button
      type="button"
      className={classnames(styles.headerNavListItem, isCondensed && styles.condensed, className)}
      {...rest}
    >
      {content}
    </button>
  );
};
